import { Component, type ReactNode } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';
import './error-boundary.css';

export function ErrorPage({ notFound = false }: { notFound?: boolean }) {
  const household =
    typeof window !== 'undefined'
      ? window.location.pathname.match(/^\/h\/([a-f\d]{24})(?:\/|$)/i)?.[1]
      : undefined;
  const home = household ? `/h/${household}/month` : '/';
  return (
    <main className="recovery-page">
      <a className="recovery-brand" href={home} aria-label="FinBob home">
        Fin<span>Bob</span>
        <span className="recovery-brand-dot" />
      </a>
      <section className="recovery-card" aria-labelledby="recovery-title">
        <div className="recovery-art" aria-hidden="true">
          <div className="recovery-orbit" />
          <div className="recovery-orbit recovery-orbit-inner" />
          <svg viewBox="0 0 160 150" fill="none">
            <rect
              x="37"
              y="19"
              width="88"
              height="112"
              rx="16"
              fill="var(--surface-1)"
              stroke="var(--border)"
              strokeWidth="2"
              transform="rotate(8 80 75)"
            />
            <rect
              x="29"
              y="15"
              width="88"
              height="112"
              rx="16"
              fill="var(--surface-2)"
              stroke="var(--accent)"
              strokeWidth="2"
            />
            <path
              d="M47 42h49M47 55h29"
              stroke="var(--text-muted)"
              strokeWidth="5"
              strokeLinecap="round"
            />
            <path
              d="m47 98 13-14 13 6 22-24"
              stroke="var(--accent)"
              strokeWidth="5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle
              cx="117"
              cy="115"
              r="23"
              fill="var(--surface-1)"
              stroke="var(--accent)"
              strokeWidth="2"
            />
            <path
              d="M108 116a9 9 0 1 0 3-8m-3-2v7h7"
              stroke="var(--accent)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="recovery-spark recovery-spark-one">＋</span>
          <span className="recovery-spark recovery-spark-two">✦</span>
        </div>
        <p className="recovery-eyebrow">
          {notFound ? 'A SMALL DETOUR · 404' : 'LET’S GET YOU BACK ON TRACK'}
        </p>
        <h1 id="recovery-title">
          {notFound ? 'This page has moved out.' : 'A little pause. A fresh start.'}
        </h1>
        <p className="recovery-description">
          {notFound
            ? 'We couldn’t find the page you’re looking for. Your next stop is just a click away.'
            : 'FinBob couldn’t load this screen. Try reloading, or head home and pick up from there.'}
        </p>
        <div className="recovery-actions">
          {!notFound && (
            <button type="button" onClick={() => window.location.reload()}>
              Reload page <span aria-hidden="true">↻</span>
            </button>
          )}
          <a className={notFound ? 'recovery-primary' : ''} href={home}>
            Back to home <span aria-hidden="true">→</span>
          </a>
        </div>
        {!notFound && (
          <div className="recovery-note">
            <span aria-hidden="true">ⓘ</span>
            <p>
              If you were saving a payment or investment, check its status after reloading before
              trying again.
            </p>
          </div>
        )}
      </section>
      <p className="recovery-footer">
        {notFound
          ? 'Check the address, or start again from home.'
          : 'Still happening? Close this tab and reopen FinBob.'}
      </p>
    </main>
  );
}

export function RouteErrorBoundary() {
  const error = useRouteError();
  return <ErrorPage notFound={isRouteErrorResponse(error) && error.status === 404} />;
}

/** Catches failures outside the router too, including application providers. */
export class AppErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override render() {
    return this.state.failed ? <ErrorPage /> : this.props.children;
  }
}
